/**
 * 報酬台帳レコード 編集モーダル (v3.2.8 / T6 Phase 2)。
 *
 * - 支払先口座選択 (admins から取得、★既定が自動選択)
 * - 額調整 (overide)
 * - メモ
 */
import type { BankAccount } from '@goenhub/shared';
import { BANK_ACCOUNT_TYPE_LABELS } from '@goenhub/shared';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input, Label, Textarea } from '@/components/ui/input';
import { useAdmin } from '@/hooks/useAdmins';
import {
  useUpdateCommissionRecord,
  type CommissionRecordWithRef,
} from '@/hooks/useCommissionRecords';

export function EditRecordDialog({
  record,
  onClose,
}: {
  record: CommissionRecordWithRef | null;
  onClose: () => void;
}) {
  const isAdminRecipient =
    record?.recipientType === 'sa' || record?.recipientType === 'employee';
  const admin = useAdmin(isAdminRecipient ? record?.recipientId : null);
  const update = useUpdateCommissionRecord();

  const [bankAccountId, setBankAccountId] = useState<string>('');
  const [finalAmountStr, setFinalAmountStr] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  useEffect(() => {
    if (!record) return;
    setBankAccountId(record.bankAccountId ?? '');
    setFinalAmountStr(String(record.finalAmount));
    setNotes(record.notes ?? '');
  }, [record]);

  if (!record) return null;

  const accounts: BankAccount[] = admin.data?.bankAccounts ?? [];

  const handleSave = async () => {
    const finalAmount = Number(finalAmountStr.replace(/[,¥\s]/g, ''));
    if (!Number.isFinite(finalAmount) || finalAmount < 0) {
      toast.error('報酬額は 0 以上の数値');
      return;
    }
    try {
      await update.mutateAsync({
        recordPath: record.path,
        bankAccountId: isAdminRecipient ? (bankAccountId || null) : undefined,
        finalAmountOverride:
          finalAmount !== record.finalAmount ? finalAmount : undefined,
        notes: notes !== (record.notes ?? '') ? notes : undefined,
      });
      toast.success('レコードを更新しました');
      onClose();
    } catch (err) {
      toast.error(`失敗: ${(err as Error).message}`);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>報酬レコード編集</DialogTitle>
          <DialogDescription>
            {record.dealTitle} / {record.recipientName} ({record.productName})
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* 計算詳細 (読み取りのみ) */}
          <div className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3 text-[11px] text-[var(--text-muted)] space-y-0.5">
            <div>セット: {record.commissionSetName}</div>
            <div>商品税抜: ¥{record.basePriceExcludingTax.toLocaleString()}</div>
            <div>計算: {record.formula}</div>
          </div>

          {/* 支払先口座 (sa/employee のみ) */}
          {isAdminRecipient && (
            <div>
              <Label className="text-xs">支払先振込口座</Label>
              {admin.isLoading ? (
                <p className="text-[11px] text-[var(--text-muted)] italic">
                  口座リストを取得中…
                </p>
              ) : accounts.length === 0 ? (
                <p className="text-[11px] text-[var(--accent-amber,_#d4a373)]">
                  この受取人は振込口座が未登録です。先にユーザー詳細画面で登録してください。
                </p>
              ) : (
                <select
                  value={bankAccountId}
                  onChange={(e) => setBankAccountId(e.target.value)}
                  className="mt-1 w-full px-3 py-2 text-xs rounded-sm border border-[var(--border)] bg-[var(--bg-input)]"
                >
                  <option value="">未選択</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.isDefault ? '★ ' : ''}
                      {a.label ? `${a.label} / ` : ''}
                      {a.bankName} {a.branchName} /{' '}
                      {BANK_ACCOUNT_TYPE_LABELS[a.accountType]} {a.accountNumber} (
                      {a.accountHolder})
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* 額調整 */}
          <div>
            <Label className="text-xs">報酬額 (調整可)</Label>
            <Input
              type="text"
              inputMode="numeric"
              value={finalAmountStr}
              onChange={(e) => setFinalAmountStr(e.target.value)}
            />
            <p className="text-[10px] text-[var(--text-muted)] mt-1">
              元の値: ¥{record.finalAmount.toLocaleString()}
            </p>
          </div>

          {/* メモ */}
          <div>
            <Label className="text-xs">メモ (任意)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="例: 手数料調整など"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            キャンセル
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={update.isPending}
          >
            {update.isPending ? '保存中…' : '保存'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
